import React, { memo, useState, useEffect } from 'react';
import { Row, Spinner, Col, Table, Badge } from 'react-bootstrap';
import { getFrames } from 'services/stillageDashboard';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import './sticky-table-header.css';
import { getImageUrl } from 'services/frameImage';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';

const FrameTable = ({ frames }) => {
    return (
        <Row className='p-3'>
            <h3>Frames</h3>
            <Col className='sticky-container'>
                <div className='sticky-inner'>
                    <Table hover size='sm' bordered className='table-sm sticky-table'>
                        <thead>
                            <tr>
                                <th className='bg-light'></th>
                                <th className='bg-light'>Job No.</th>
                                <th className='bg-light'>Frame ID</th>
                                <th className='bg-light'>Location</th>
                                <th className='bg-light text-center'>Fitted?</th>
                                <th className='bg-light'>Fitted Date</th>
                                <th className='bg-light'>Fitted By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {frames?.map(frame => {
                                const fitted = frame.isFitted;
                                let dateString = '';
                                if (frame.fittingDateStart || frame.fittedDate) {
                                    const date = frame.fittingDateStart ? frame.fittingDateStart : frame.fittedDate;
                                    dateString = dayjs(date).format('DD/MM/YYYY');
                                }
                                return (<tr key={`${frame.jobNo}-${frame.frameID}`} className={fitted ? 'table-success' : 'table-danger'}>
                                    <td>
                                        <img
                                            src={getImageUrl({ jobNo: frame.jobNo, winNo: frame.winno })}
                                            alt={`Frame ${frame.jobNo}-${frame.frameID}`} />
                                    </td>
                                    <td>{frame.jobNo}</td>
                                    <td>{frame.frameID}</td>
                                    <td>{frame.locationA}</td>
                                    <td className='text-center'>{fitted ? '✔️' : '❌'}</td>
                                    <td>{dateString}</td>
                                    <td>{frame.fittedByString || ''}</td>
                                </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </div>
            </Col>
        </Row>
    );
}

const BoxTable = ({ boxes }) => {
    return (
        <Row className='p-3'>
            <h3>Boxes</h3>
            <Col className='sticky-container'>
                <div className='sticky-inner'>
                    <Table hover size='sm' bordered className='table-sm sticky-table'>
                        <thead>
                            <tr>
                                <th className='bg-light'>Job No.</th>
                                <th className='bg-light'>Box ID</th>
                                <th className='bg-light'>Box Type</th>
                                <th className='bg-light'>Description</th>
                                <th className='bg-light'>Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {boxes?.map(boxGroup => {
                                const jobNo = boxGroup[0][0]?.jobNo;
                                const jobRows = boxGroup.reduce((acc, box) => acc += box?.length, 0);

                                return boxGroup.map((boxList, boxListIdx) => {
                                    const boxRows = boxList?.length;
                                    return boxList.map((box, boxIdx) => {
                                        const boxId = box.boxId;
                                        let icon = '';

                                        switch (box.boxType) {
                                            case 'One Off Extras':
                                                icon = '1️⃣';
                                                break;
                                            case 'Boxes':
                                                icon = '📦';
                                                break;
                                            case 'Ancillary':
                                                icon = '🧰';
                                                break;
                                            case 'Bay Cill':
                                                icon = '📁';
                                                break;
                                        }

                                        return <tr key={`${boxListIdx}-${boxIdx}`}>
                                            {(boxListIdx === 0 && boxIdx === 0) && (
                                                <td rowSpan={jobRows} className='text-center align-middle p-0'>{jobNo}</td>
                                            )}
                                            {(boxIdx === 0) && (
                                                <td rowSpan={boxRows} className='text-center align-middle p-0'>{boxId}</td>
                                            )}
                                            <td className='p-1 text-center'><Badge bg="secondary">{box.boxType} {icon}</Badge></td>
                                            <td className='p-1'>{box.description}</td>
                                            <td className='p-1 text-center'><Badge bg="success">{box.qty}</Badge></td>
                                        </tr>
                                    });
                                })
                            })}
                        </tbody>
                    </Table>
                </div>
            </Col>
        </Row>
    );
}

const FrameDetails = ({ showModal, jobs, stillageNo }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!showModal || !jobs || !stillageNo) {
            setData(null);
            setLoading(false);
            return;
        }

        // Captured locally so a superseded request can tell it is no longer the current one
        const controller = new AbortController();
        setLoading(true);

        getFrames(jobs, stillageNo, controller.signal)
            .then(res => {
                if (controller.signal.aborted) return;
                setData(res);
            })
            .catch(error => {
                if (controller.signal.aborted) return;
                setData(null);
                toast.error(`Error fetching frames: ${error.message}`);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [showModal, jobs, stillageNo]);

    if (loading)
        return (
            <div className='justify-content-center d-flex w-100 my-5'>
                <Spinner animation="border" role="status" variant='primary' />
            </div>
        );

    if (!data && !loading)
        return (
            <div className='h-100 text-center align-middle my-4'>No data</div>
        );

    const handlePrint = () => {
        const w = window.open('', '_blank');
        w.document.body.innerHTML = document.getElementById('frame-details').innerHTML;
        w.document.close();
        w.focus();
        w.print();
        w.close();
    }

    return (
        <>
            <div className='d-flex justify-content-end pt-3'>
                <button title="Print Details" className="btn btn-outline-info" onClick={handlePrint}>
                    <FontAwesomeIcon icon={faPrint} />
                </button>
            </div>
            <div id='frame-details'>
                {data?.frames?.length ? <FrameTable frames={data.frames} /> : <></>}
                {data?.boxes?.length ? <BoxTable boxes={data.boxes} /> : <></>}
            </div>
        </>
    );
}

FrameDetails.propTypes = {
    showModal: PropTypes.bool,
    jobs: PropTypes.string,
    stillageNo: PropTypes.number
};
FrameTable.propTypes = {
    frames: PropTypes.array
};
BoxTable.propTypes = {
    boxes: PropTypes.array
};

export default memo(FrameDetails);